import { CsvEnum, Id, IsoDateTime, ListQuery, Paginated, QueryBool, QueryInt, defineAction, defineEvent, z } from '@mkcore/shared';
import { approvalPermissions as P } from './permissions';

// Namespace approval (docs/01-architectuur/05-api-en-mcp.md): the Approval entity from U1 for the invoice gate
// (CO-0928, CO-0340), and from U3 the inbox (CO-0931) with the calls of agents that wait for a person (CO-0930).

export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'revise', 'withdrawn', 'superseded'] as const;
export const APPROVAL_DECISIONS = ['approve', 'reject', 'revise'] as const;
/** Where a decision came from (CO-0935): the app, the API or MCP, or a signed link in a mail, a Teams card or a Hermes chat. */
export const DECIDED_VIAS = ['app', 'api', 'mail', 'teams', 'hermes'] as const;
/** The channels of a signed link to decide (CO-0935). */
export const LINK_VIAS = ['mail', 'teams', 'hermes'] as const;
export const LINK_STATUSES = ['valid', 'expired', 'invalid'] as const;
/**
 * What approving a request risks (CO-1394): it goes outside (external), cannot be undone (destructive) or touches money.
 * From the kind (its handler says so) and, for the call of an agent, the kind of the tool. Approving in bulk is refused
 * for a request with any of them.
 */
export const APPROVAL_RISKS = ['external', 'destructive', 'money'] as const;
export const APPROVAL_GROUP_BYS = ['requester', 'kind'] as const;
/** The most requests approval.request.decideMany decides in one call (CO-1394). */
export const DECIDE_MANY_MAX = 50;
/** How far back the approval rate of a requester or kind looks (CO-1394). */
export const APPROVAL_RATE_DAYS = 90;
/** The kind of a form a person fills in for a job or an agent (CO-0932). */
export const FORM_KIND = 'form';
export const FORM_FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'choice', 'boolean'] as const;
/** The choice list of reasons to reject or send back a request (CO-1393); managed like any choice list. */
export const DECISION_REASON_LIST = 'approval.decisionReason';
/** The reason that needs a comment (CO-1393). */
export const OTHER_REASON = 'other';
/** A reason code of approval.decisionReason, such as wrong-content (CO-1393). */
export const ReasonCode = z.string().trim().min(1).max(100).describe('A value of the choice list approval.decisionReason, such as wrong-content or other');

/** A field of a form (CO-0932): what the person fills in before a job or an agent goes on. */
export const FormField = z
  .object({
    name: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/, 'A name such as scope or budgetCents'),
    label: z.string().min(1).max(200),
    type: z.enum(FORM_FIELD_TYPES),
    required: z.boolean().default(false),
    options: z.array(z.string().min(1).max(200)).max(50).optional().describe('choice: the values to choose from'),
    default: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
    help: z.string().max(500).optional(),
  })
  .openapi('FormField');
export type FormFieldValue = z.output<typeof FormField>;

export const Approval = z
  .object({
    id: Id,
    kind: z.string().describe('What is asked: invoice, credit_note, mail, …'),
    targetType: z.string(),
    targetId: Id,
    title: z.string(),
    summary: z.string(),
    status: z.enum(APPROVAL_STATUSES).describe('withdrawn: the requester took it back before a decision; superseded: the draft changed after the request, a new request replaces it (CO-0933)'),
    requestedBy: Id,
    requestedByName: z.string(),
    requestedByKind: z.enum(['human', 'agent', 'system']),
    decidedBy: Id.nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: IsoDateTime.nullable(),
    decidedVia: z.enum(DECIDED_VIAS).nullable().describe('Where the decision came from (CO-0935); empty for decisions before U9'),
    comment: z.string(),
    assigneeId: Id.nullable().describe('The intended decider, set by the kind (CO-1388), such as the requester of a task for the claim of an agent; anyone with the permission may still decide'),
    assigneeName: z.string().nullable(),
    dueAt: IsoDateTime.nullable().describe('When the decision is due, set by the kind (CO-1388)'),
    reasonCode: z.string().nullable().describe('reject and revise: the reason from the choice list approval.decisionReason (CO-1393); empty before U9 and for other decisions'),
    toolCall: z
      .object({ tool: z.string(), input: z.record(z.string(), z.unknown()), agentRunId: Id.nullable(), toolKind: z.string() })
      .nullable()
      .describe('kind agent.tool: the call of the agent that waits (CO-0930)'),
    result: z.unknown().nullable().describe('kind agent.tool: what the approved call answered'),
    resultError: z.string().nullable().describe('kind agent.tool: why the approved call failed'),
    executedAt: IsoDateTime.nullable(),
    agentRunId: Id.nullable().describe('The agent run whose draft this is (CO-0853); its decision becomes feedback on that run'),
    form: z.array(FormField).nullable().describe('kind form: the fields the person fills in (CO-0932)'),
    values: z.record(z.string(), z.unknown()).nullable().describe('kind form: what the person filled in'),
    url: z.string().describe('Deep link to the object in the web app'),
    risks: z.array(z.enum(APPROVAL_RISKS)).describe('What approving risks (CO-1394): external, destructive (cannot be undone) or money; approving in bulk is refused when not empty'),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('Approval');

/** Decided requests of the last APPROVAL_RATE_DAYS days, and the share approved (CO-1394). */
const RateFields = {
  decided: z.number().int().describe('Approved, rejected or sent back in the last 90 days'),
  approved: z.number().int(),
  approvalRateBp: z.number().int().nullable().describe('approved of decided, in basis points; null without decisions'),
};

export const ApprovalGroup = z
  .object({
    key: z.string().describe('groupBy requester: the id of the requester (system for the system); kind: the kind'),
    requestedBy: Id.nullable(),
    requestedByName: z.string().nullable(),
    requestedByKind: z.enum(['human', 'agent', 'system']).nullable(),
    kind: z.string().nullable(),
    count: z.number().int().describe('Requests in the group with these filters, over all pages'),
    risks: z.array(z.enum(APPROVAL_RISKS)).describe('The risks of the kinds in the group (for a kind; a call of an agent adds the kind of its tool per row)'),
    ...RateFields,
  })
  .openapi('ApprovalGroup');

export const ApprovalRate = z.object({ requestedBy: Id, kind: z.string(), ...RateFields }).openapi('ApprovalRate');

export const listApprovals = defineAction({
  name: 'approval.request.list',
  method: 'get',
  path: '/approval/requests',
  permission: P.keys.read,
  input: ListQuery.extend({
    status: CsvEnum(APPROVAL_STATUSES).optional().describe('Default pending'),
    kind: z.string().max(50).optional(),
    targetId: Id.optional(),
    mine: QueryBool.optional().describe('Only the requests of the caller, such as an agent following its own calls'),
    requestedBy: Id.optional().describe('Only the requests of this person or agent, such as the agent that owns a task (CO-1377)'),
    assignee: z.union([z.literal('me'), Id]).optional().describe('Only the requests meant for this person, or me for the caller (CO-1388)'),
    groupBy: z.enum(APPROVAL_GROUP_BYS).optional().describe('Also the groups per requester or per kind, with their counts and approval rate, and the rate per requester and kind of the rows (CO-1394)'),
  }),
  output: Paginated(Approval).extend({
    groups: z.array(ApprovalGroup).optional().describe('With groupBy: the groups over all pages, largest first'),
    rates: z.array(ApprovalRate).optional().describe('With groupBy: the approval rate of each requester for each kind on this page'),
  }),
  summary: 'Approval requests, pending first and newest first (CO-0928)',
});

export const getApproval = defineAction({
  name: 'approval.request.get',
  method: 'get',
  path: '/approval/requests/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Approval,
  etag: true,
});

export const decideApproval = defineAction({
  name: 'approval.request.decide',
  method: 'post',
  path: '/approval/requests/{id}/decide',
  permission: P.keys.approve,
  input: z
    .object({
      id: Id,
      decision: z.enum(APPROVAL_DECISIONS),
      reasonCode: ReasonCode.optional().describe('reject and revise: a value of the choice list approval.decisionReason (CO-1393); ignored for approve'),
      comment: z.string().max(2000).optional().describe('Required for reject and revise without a reasonCode, and for the reason other; the requester (or the agent) reads it'),
      input: z.record(z.string(), z.unknown()).optional().describe('kind agent.tool: the arguments as approved, when a person changed them'),
      values: z.record(z.string(), z.unknown()).optional().describe('A request with a form (kind form, agent.budget): the filled-in fields; checked against the form'),
      link: z
        .object({ via: z.enum(LINK_VIAS), exp: z.number().int().positive(), sig: z.string().max(200) })
        .strict()
        .optional()
        .describe('The signed link the person came from (CO-0935): when valid for the caller, the history says mail, teams or hermes'),
    })
    .strict(),
  output: Approval,
  successStatus: 200,
  summary: 'Decide a pending request: approve runs the gated action, reject and revise send it back with a reason',
  description:
    'Only a person with the approve permission of this module decides; an agent identity never does. Fails with approval-decided when it was decided already. ' +
    'Reject and revise give a reasonCode from the choice list approval.decisionReason (approval-reason-unknown otherwise) or a comment; the reason other needs a comment too (comment-required). ' +
    'For kind agent.tool, approve runs the call as the person who decides (with the changed input if given) and stores its result. ' +
    'For a request with a form (kind form, agent.budget), approve takes its values and needs the required ones (form-invalid otherwise). When the draft changed after the request, ' +
    'the request becomes superseded and a new one waits (CO-0933).',
});

export const checkLink = defineAction({
  name: 'approval.link.check',
  method: 'get',
  path: '/approval/requests/{id}/link',
  permission: P.keys.read,
  restOnly: true,
  input: z.object({ id: Id, via: z.enum(LINK_VIAS), exp: QueryInt.pipe(z.number().int().positive()), sig: z.string().max(200) }),
  output: z.object({ status: z.enum(LINK_STATUSES), expiresAt: IsoDateTime }).openapi('ApprovalLinkCheck'),
  summary: 'Checks a signed link to decide (CO-0935): valid for the caller, expired, or invalid (changed, or for someone else)',
  description: 'The link from a notification mail, a Teams card or a Hermes chat. It grants nothing: deciding still needs the login and the approve permission.',
});

export const requestForm = defineAction({
  name: 'approval.form.request',
  method: 'post',
  path: '/approval/forms',
  permission: P.keys.read,
  input: z
    .object({
      title: z.string().trim().min(1).max(300),
      summary: z.string().max(4000).optional().describe('What the person decides and why'),
      fields: z.array(FormField).min(1).max(30),
      targetType: z.string().trim().min(1).max(50).optional().describe('The object the form is about, such as lead; default form'),
      targetId: Id.optional().describe('Its id; default a new one'),
    })
    .strict(),
  output: Approval,
  summary: 'Ask a person to fill in a form in the approval inbox, such as a scope to confirm (CO-0932)',
  description: 'Any caller may ask, also an agent; a person with approvals:approve fills it in. Read the answer with approval.request.get, or start a routine on approval.request.decided.',
});

export const approvalRequested = defineEvent({
  type: 'approval.request.created',
  version: 1,
  payload: z.object({ approvalId: Id, kind: z.string(), targetType: z.string(), targetId: Id, requestedBy: Id }),
});

export const approvalDecided = defineEvent({
  type: 'approval.request.decided',
  version: 1,
  payload: z.object({
    approvalId: Id,
    kind: z.string(),
    targetType: z.string(),
    targetId: Id,
    decision: z.enum([...APPROVAL_DECISIONS, 'withdrawn', 'superseded']),
    decidedBy: Id,
    comment: z.string().default(''),
    /** reject and revise: the reason from approval.decisionReason (CO-1393); null for older events and other decisions. */
    reasonCode: z.string().nullable().default(null),
    /** kind form: what the person filled in (CO-0932). */
    values: z.record(z.string(), z.unknown()).nullable().default(null),
    /** The workflow waiting for this decision, if any (CO-0929). */
    workflowId: z.string().nullable().default(null),
  }),
});

export const DecideManyResult = z
  .object({
    id: Id,
    ok: z.boolean(),
    status: z.enum(APPROVAL_STATUSES).nullable().describe('The status after the call; null when the request is not there'),
    error: z.object({ code: z.string(), detail: z.string() }).nullable().describe('Why this one was not decided, such as approval-bulk-refused or approval-decided'),
  })
  .openapi('ApprovalDecideManyResult');

export const decideManyApprovals = defineAction({
  name: 'approval.request.decideMany',
  method: 'post',
  path: '/approval/requests/decide-many',
  permission: P.keys.approve,
  input: z
    .object({
      ids: z.array(Id).min(1).max(DECIDE_MANY_MAX).describe('Up to 50 pending requests'),
      decision: z.enum(APPROVAL_DECISIONS),
      reasonCode: ReasonCode.optional().describe('reject and revise: one reason for all of them (CO-1393); without it the comment is required'),
      comment: z.string().max(2000).optional().describe('One comment for all of them; required for reject and revise without a reasonCode, and for the reason other'),
    })
    .strict(),
  output: z.object({ results: z.array(DecideManyResult), decided: z.number().int(), failed: z.number().int() }).openapi('ApprovalDecideMany'),
  successStatus: 200,
  summary: 'Decide up to 50 pending requests at once, each on its own; approving in bulk is refused for external, destructive or money requests (CO-1394)',
  description:
    'One decision, reason and comment for all ids. Each request is decided in its own transaction, as approval.request.decide would, and gets its own result: ' +
    'one that fails (approval-decided, forbidden, form-invalid) does not stop the others. approve refuses a request whose risks are not empty ' +
    '(approval-bulk-refused): decide it on its own with approval.request.decide. reject and revise are always allowed. ' +
    'A retry of the same call is safe: a request this person decided the same way already counts as ok. Only a person decides; an agent identity never does.',
});

export const approvalActions = [listApprovals, getApproval, decideApproval, decideManyApprovals, requestForm];
export const approvalEvents = [approvalRequested, approvalDecided];
